/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';

import { apiClient, apiQuery } from '../../state';
import { IntegrationConfigureGenericFlow } from './IntegrationConfigureGenericFlow';
import { assertIsExtendedSiteInstallation } from './integrations.utils';

export interface IntegrationConfigureSiteFlowProps {
    /** Integration to configure */
    integration: api.Integration;
    /** Installation of the integration in the site */
    siteInstallation: api.IntegrationSiteInstallation;
    /** Callback when the configuration is finished */
    onComplete?: () => void;
}

/**
 * Component to render the configuration flow for an integration in a site.
 */
export function IntegrationConfigureSiteFlowById(props: {
    integrationName: string;
    organizationId: string;
    siteId: string;
    onComplete?: () => void;
}) {
    const { integrationName, organizationId, siteId, onComplete } = props;

    const [{ data: integration }, { data: siteInstallation }] = useSuspenseQueries({
        queries: [
            apiQuery('getIntegrationByName', { integrationName }),
            apiQuery('getIntegrationSiteInstallation', {
                installationId: getIntegrationInstallationKey(integrationName, {
                    organization: organizationId,
                }),
                siteId,
                integrationName,
                extended: true,
            }),
        ] as const,
    });

    return (
        <IntegrationConfigureSiteFlow
            integration={integration}
            siteInstallation={siteInstallation}
            onComplete={onComplete}
        />
    );
}

/**
 * Component to render the configuration flow for an integration in a site.
 * The `siteInstallation` should be live-updated with the configuration changes, consider using <IntegrationConfigureSiteFlowById />.
 */
export function IntegrationConfigureSiteFlow(
    props: IntegrationConfigureSiteFlowProps
): React.ReactElement {
    const { integration, siteInstallation, onComplete } = props;
    assertIsExtendedSiteInstallation(siteInstallation);

    const configuration = integration.configurations?.site;
    if (!configuration) {
        throw new Error(`Expected a site configuration for integration ${integration.name}`);
    }

    return (
        <IntegrationConfigureGenericFlow
            integration={integration}
            onComplete={() => {
                analytics.logEvent(
                    analytics.createSiteEvent(
                        {
                            id: siteInstallation.site.id,
                        },
                        {
                            type: 'integration_configuration_complete',
                            integration: integration.name,
                        }
                    )
                );

                onComplete?.();
            }}
            // We need the useMemo to ensure ContentKit doesn't re-render on every parent render
            {...React.useMemo(
                () => ({
                    configuration,
                    contentKitProps: {
                        siteInstallation: {
                            configuration: siteInstallation.configuration,
                        },
                    },
                    contentKitContext: {
                        type: 'configuration_site',
                        siteId: siteInstallation.site.id,
                        editable: true,
                    },
                    formValue: siteInstallation.configuration,
                    formBaseUrl: siteInstallation.urls.publicEndpoint,
                    formOnChangeValue: async (value) => {
                        await apiClient.integrations.updateIntegrationSiteInstallation(
                            integration.name,
                            siteInstallation.installation,
                            siteInstallation.site.id,
                            {
                                configuration: value,
                            },
                            { extended: true }
                        );
                    },
                }),
                [configuration, siteInstallation.configuration]
            )}
        />
    );
}
